/**
 * /health — Health Monitor cockpit.
 *
 * Dense, scannable table of all currently boarding pets at the active facility.
 * Status lights: green = fine, amber = checkout within 24h, red = overdue.
 * Auto-refreshes every 60 seconds. Inline "Log note" textarea per row.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  ExternalLink,
  Phone,
  Pill,
  RefreshCw,
  StickyNote,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface MonitorRow {
  bookingId: string;
  pet: {
    id: string;
    name: string;
    species: string;
    breed: string | null;
    ageYears: number | null;
    medicalConditions: string | null;
    feedingInstructions: string | null;
    behaviorNotes: string | null;
  };
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    emergencyContactName: string | null;
    emergencyContactPhone: string | null;
  };
  run: { id: string; name: string } | null;
  checkIn: string;
  checkout: string;
  daysInCare: number;
  statusLight: 'green' | 'amber' | 'red';
  medication: string | null;
  latestNote: string | null;
  latestDailyCheck: string | null;
  welfareStatus: 'ok' | 'concern' | null;
}

interface MonitorResponse {
  rows: MonitorRow[];
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function StatusLight({
  light,
  welfare,
}: {
  light: 'green' | 'amber' | 'red';
  welfare?: 'ok' | 'concern' | null;
}) {
  if (light === 'red')
    return (
      <span title="Overdue — past scheduled checkout">
        <AlertTriangle className="size-4 text-destructive" strokeWidth={2} />
      </span>
    );
  if (light === 'amber')
    return (
      <span title="Due out within 24 hours">
        <Clock className="size-4 text-warning-foreground" strokeWidth={2} />
      </span>
    );
  if (welfare === 'concern')
    return (
      <span title="Welfare concern from daily check">
        <AlertTriangle className="size-4 text-amber-500" strokeWidth={2} />
      </span>
    );
  return (
    <span title="In care — all fine">
      <CheckCircle2 className="size-4 text-success" strokeWidth={2} />
    </span>
  );
}

function NoteCell({ bookingId }: { bookingId: string }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleOpen() {
    setOpen(true);
    setSaved(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  async function handleSave() {
    if (!note.trim()) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await fetch(`/api/bookings/${bookingId}/observations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ traits: ['other'], notes: note.trim() }),
      });
      setSaved(true);
      setNote('');
      setTimeout(() => {
        setOpen(false);
        setSaved(false);
      }, 1200);
    } catch {
      // ignore — best-effort
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="press rounded px-2 py-1 text-[11px] font-medium text-muted-foreground ring-1 ring-border transition-colors hover:text-foreground"
      >
        Log note
      </button>
    );
  }

  return (
    <div className="flex items-end gap-1.5">
      <textarea
        ref={textareaRef}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        className="w-[160px] resize-none rounded border border-border bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
        placeholder="Note…"
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void handleSave();
        }}
      />
      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSave()}
          className="press rounded bg-primary px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
        >
          {saved ? 'Saved' : saving ? '…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="press rounded px-2 py-1 text-[11px] text-muted-foreground ring-1 ring-border"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function HealthMonitorPage() {
  const { state } = useAuthContext();
  const noun = useSpaceNoun();
  const facilityId = state.me?.activeFacility?.id ?? '';
  const facilityName = state.me?.activeFacility?.name ?? 'Facility';

  const [rows, setRows] = useState<MonitorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await fetch(`/api/health/monitor?facilityId=${facilityId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as MonitorResponse;
      setRows(data.rows ?? []);
      setLastRefresh(new Date());
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [facilityId]);

  // Initial load
  useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const redCount = rows.filter((r) => r.statusLight === 'red').length;
  const amberCount = rows.filter((r) => r.statusLight === 'amber').length;

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10 md:py-14">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {facilityName}
          </p>
          <h1 className="mt-1 flex items-center gap-2.5 text-2xl font-medium tracking-tight">
            <Activity className="size-6 text-primary" strokeWidth={1.75} />
            Health Monitor
          </h1>
          {lastRefresh && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Last refreshed at{' '}
              <span className="num">
                {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>{' '}
              · auto-refreshes every 60 s
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Summary pills */}
          {redCount > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-[11px] font-medium text-destructive ring-hairline">
              <AlertTriangle className="size-3" strokeWidth={2.5} />
              <span className="num">{redCount}</span> overdue
            </span>
          )}
          {amberCount > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-warning/15 px-3 py-1 text-[11px] font-medium text-warning-foreground ring-hairline">
              <Clock className="size-3" strokeWidth={2.5} />
              <span className="num">{amberCount}</span> due out soon
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={refreshing}
            className="press flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium text-foreground ring-1 ring-border transition-opacity disabled:opacity-50"
          >
            <RefreshCw className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Loading skeleton ─────────────────────────────────────────── */}
      {loading && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.15 }}
            />
          ))}
        </div>
      )}

      {/* ── Error ────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div className="rounded-xl bg-destructive/10 px-6 py-4 text-sm text-destructive ring-1 ring-destructive/20">
          Failed to load health monitor: {error}
        </div>
      )}

      {/* ── Empty state ──────────────────────────────────────────────── */}
      {!loading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-20 text-center text-muted-foreground">
          <CheckCircle2 className="size-10 text-success/60" strokeWidth={1.5} />
          <p className="text-sm font-medium">No pets currently in care at this facility.</p>
          <p className="text-xs">Checked-in boarding stays will appear here.</p>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────── */}
      {!loading && !error && rows.length > 0 && (
        <div className="elevate overflow-x-auto rounded-xl bg-surface ring-1 ring-border">
          <table className="min-w-[720px] w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Pet
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {noun.Singular}
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Days
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Check-in
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Checkout
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Owner
                </th>
                <th className="px-4 py-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr
                  key={row.bookingId}
                  className={`group transition-colors hover:bg-secondary/30 ${
                    row.statusLight === 'red' ? 'bg-destructive/5' : ''
                  } ${row.statusLight === 'amber' ? 'bg-warning/5' : ''}`}
                >
                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusLight light={row.statusLight} welfare={row.welfareStatus} />
                  </td>

                  {/* Pet */}
                  <td className="px-4 py-3">
                    <div className="font-medium">{row.pet.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.pet.species}
                      {row.pet.breed ? ` · ${row.pet.breed}` : ''}
                      {row.pet.ageYears !== null ? (
                        <>
                          {' · '}
                          <span className="num">{row.pet.ageYears}</span>y
                        </>
                      ) : null}
                    </div>
                    {row.pet.medicalConditions && (
                      <div className="mt-0.5 max-w-[200px] truncate text-[11px] text-amber-700">
                        {row.pet.medicalConditions}
                      </div>
                    )}
                    {row.medication && (
                      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-blue-700">
                        <Pill className="size-3 flex-shrink-0" strokeWidth={2} />
                        <span className="max-w-[200px] truncate">{row.medication}</span>
                      </div>
                    )}
                  </td>

                  {/* Run */}
                  <td className="px-4 py-3">
                    {row.run ? (
                      <span className="rounded bg-secondary px-2 py-0.5 text-xs font-medium ring-hairline">
                        {row.run.name}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unassigned</span>
                    )}
                  </td>

                  {/* Days in care */}
                  <td className="px-4 py-3">
                    <span className="num font-medium">{row.daysInCare}</span>
                    <span className="ml-0.5 text-xs text-muted-foreground">d</span>
                  </td>

                  {/* Check-in */}
                  <td className="px-4 py-3 text-xs">
                    <div className="num">{formatDate(row.checkIn)}</div>
                    <div className="num text-muted-foreground">{formatTime(row.checkIn)}</div>
                  </td>

                  {/* Checkout */}
                  <td className="px-4 py-3 text-xs">
                    <div
                      className={`num font-medium ${
                        row.statusLight === 'red'
                          ? 'text-destructive'
                          : row.statusLight === 'amber'
                            ? 'text-warning-foreground'
                            : ''
                      }`}
                    >
                      {formatDate(row.checkout)}
                    </div>
                    <div className="num text-muted-foreground">{formatTime(row.checkout)}</div>
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers/${row.customer.id}`}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {row.customer.firstName} {row.customer.lastName}
                    </Link>
                    {row.customer.phone && (
                      <a
                        href={`tel:${row.customer.phone}`}
                        className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                      >
                        <Phone className="size-3" strokeWidth={2} />
                        <span className="num">{row.customer.phone}</span>
                      </a>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    {row.latestDailyCheck && (
                      <div
                        className={`mb-2 flex items-start gap-1.5 rounded px-2 py-1.5 text-[11px] ring-hairline ${
                          row.welfareStatus === 'concern'
                            ? 'bg-amber-50 ring-amber-200 text-amber-800'
                            : 'bg-secondary/60'
                        }`}
                      >
                        <ClipboardCheck className="mt-0.5 size-3 flex-shrink-0" strokeWidth={2} />
                        <span>{row.latestDailyCheck}</span>
                      </div>
                    )}
                    {row.latestNote && (
                      <div className="mb-2 flex items-start gap-1.5 rounded bg-secondary/60 px-2 py-1.5 text-[11px] ring-hairline">
                        <StickyNote
                          className="mt-0.5 size-3 flex-shrink-0 text-muted-foreground"
                          strokeWidth={2}
                        />
                        <span className="line-clamp-2 text-foreground/80">{row.latestNote}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <NoteCell bookingId={row.bookingId} />
                      <Link
                        to={`/bookings/${row.bookingId}`}
                        className="press flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-primary ring-1 ring-primary/30 hover:bg-primary/5"
                      >
                        <ExternalLink className="size-3" strokeWidth={2} />
                        View
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer count */}
          <div className="border-t border-border bg-secondary/20 px-4 py-2.5 text-xs text-muted-foreground">
            <span className="num">{rows.length}</span> pet{rows.length === 1 ? '' : 's'} in care
          </div>
        </div>
      )}
    </div>
  );
}
